/**
 * Shared time-slot utilities for booking surfaces.
 *
 * Behaviour is intentionally identical to the per-component duplicates
 * that previously existed in BookingDialog.js, PublicBooking.js, etc.
 * Do NOT change any output here without auditing every call site.
 */

/**
 * Generate a list of `HH:MM` slot strings between startHour and endHour (inclusive).
 *
 * Iterates each hour in `[startHour, endHour]` and emits one slot per
 * `intervalMinutes` step within the hour. With the 30-minute step used
 * across the app, calling `generateTimeSlots(0, 23, 30)` produces 48 slots
 * (`00:00`..`23:30`), and `generateTimeSlots(6, 23, 30)` produces 36 slots
 * (`06:00`..`23:30`).
 *
 * @param {number} startHour       Start hour (0–23)
 * @param {number} endHour         End hour (0–23), inclusive
 * @param {number} intervalMinutes Step within each hour (e.g., 30)
 * @returns {string[]} Slot strings padded to "HH:MM".
 */
export const generateTimeSlots = (startHour, endHour, intervalMinutes) => {
  const slots = [];
  for (let hour = startHour; hour <= endHour; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      slots.push(
        `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
      );
    }
  }
  return slots;
};

/**
 * Format a "HH:MM" string as 12-hour `h:MM AM/PM`.
 * Returns '' for falsy input. Preserves the original minutes substring
 * (does NOT pad single-digit minutes — matching the legacy behaviour).
 */
export const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

/**
 * Format a local-time Date as "YYYY-MM-DD" using its LOCAL components.
 *
 * Critical: DO NOT use `d.toISOString().split('T')[0]` for "today" /
 * "start of month" / any user-facing date range — that converts the
 * local Date to UTC and, at IST timezones (UTC+5:30), rolls the date
 * BACK by one day whenever the local time is before 05:30. That was the
 * root cause of the "This Month starts on the 31st of prev month" bug.
 *
 * @param {Date} d
 * @returns {string} "YYYY-MM-DD" in the browser's LOCAL timezone.
 */
export const toLocalYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Return true if `timeStr` is in the past for the given local `selectedDate`.
 * Only checks against "today" — past dates return false (the existing
 * behaviour callers depend on).
 *
 * @param {string} selectedDate "YYYY-MM-DD" in local time
 * @param {string} timeStr      "HH:MM" 24-hour
 */
export const isSlotPassed = (selectedDate, timeStr) => {
  const today = toLocalYMD(new Date());  // local, not UTC — fixes IST rollover
  if (selectedDate !== today) return false; // Only check for today

  const now = new Date();
  const [hours, minutes] = timeStr.split(':').map(Number);
  const slotTime = new Date();
  slotTime.setHours(hours, minutes, 0, 0);

  return slotTime <= now;
};

/**
 * Convert "HH:MM" to minutes-since-midnight.
 */
export const timeToMin = (t) => {
  const [h, m] = String(t).split(':').map(Number);
  return h * 60 + m;
};

/**
 * Anchored minute conversion: if `t` sorts before `anchor` (i.e. rolls
 * past midnight relative to the anchor), add a day's worth of minutes so
 * a chronological compare handles a 23:00 → 00:30 range as contiguous.
 *
 * Extracted from BookingGrid.jsx so the calendar/grid AND BulkBooking use
 * ONE copy of the time-math (no drift possible).
 *
 * @param {string} t       "HH:MM"
 * @param {string} anchor  "HH:MM" that anchors the ordering (usually the
 *                         start of the range being compared)
 */
export const toMinAnchored = (t, anchor) => {
  const raw = timeToMin(t);
  if (!anchor) return raw;
  return raw < timeToMin(anchor) ? raw + 1440 : raw;
};
